// src/admin/AIFraudDashboard.jsx
// AI Fraud Detection Dashboard for Admins
// WARNING-ONLY MODE: Shows alerts but doesn't restrict users

import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  Shield, AlertTriangle, Activity, TrendingUp, 
  Users, DollarSign, Star, RefreshCw, Check,
  Eye, Clock, AlertCircle, ChevronDown, ChevronUp,
  FileText, UserCheck, XCircle, Settings
} from 'lucide-react';
import AIFraudSettings from './AIFraudSettings';

const AIFraudDashboard = () => {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [filter, setFilter] = useState('pending_review');
  const [expandedAlerts, setExpandedAlerts] = useState({});
  const [activeView, setActiveView] = useState('alerts'); // 'alerts' or 'settings'

  const functions = getFunctions();

  useEffect(() => {
    loadFraudAlerts();
    // Auto-refresh every 5 minutes
    const interval = setInterval(loadFraudAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [filter]);

  const loadFraudAlerts = async () => {
    try {
      setLoading(true);
      const getFraudAlerts = httpsCallable(functions, 'getFraudAlerts');
      const result = await getFraudAlerts({ status: filter });
      
      if (result.data.success) {
        setAlerts(result.data.alerts);
        setStats(result.data.stats);
      }
    } catch (error) {
      console.error('Error loading fraud alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const reviewAlert = async (alertId, action) => {
    try {
      const reviewFraudAlert = httpsCallable(functions, 'reviewFraudAlert');
      const result = await reviewFraudAlert({
        alertId,
        action,
        notes: `Reviewed by admin at ${new Date().toLocaleString()}`
      });
      
      if (result.data.success) {
        loadFraudAlerts();
        setSelectedAlert(null);
      }
    } catch (error) {
      console.error('Error reviewing alert:', error);
    }
  };

  const getRiskColor = (level) => {
    switch(level) {
      case 'CRITICAL': return 'text-red-600 bg-red-50 border-red-200';
      case 'HIGH': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'MEDIUM': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'LOW': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getRiskIcon = (level) => {
    switch(level) {
      case 'CRITICAL': return <XCircle className="w-5 h-5" />;
      case 'HIGH': return <AlertTriangle className="w-5 h-5" />;
      case 'MEDIUM': return <AlertCircle className="w-5 h-5" />;
      case 'LOW': return <Activity className="w-5 h-5" />;
      default: return <Shield className="w-5 h-5" />;
    }
  };

  const toggleAlertExpansion = (alertId) => {
    setExpandedAlerts(prev => ({
      ...prev,
      [alertId]: !prev[alertId]
    }));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg p-6 mb-8 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Shield className="w-8 h-8 mr-3" />
            <div>
              <h1 className="text-2xl font-bold">AI Fraud Detection</h1>
              <p className="text-purple-100">Warning-Only Mode: Monitoring for suspicious patterns</p>
            </div>
          </div>
          <button
            onClick={loadFraudAlerts}
            className="flex items-center px-4 py-2 bg-white text-purple-600 rounded-lg hover:bg-purple-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setActiveView('alerts')}
          className={`flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${
            activeView === 'alerts' 
              ? 'bg-purple-600 text-white' 
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          <Shield className="w-5 h-5 mr-2" />
          Fraud Alerts
        </button>
        <button
          onClick={() => setActiveView('settings')}
          className={`flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${
            activeView === 'settings' 
              ? 'bg-purple-600 text-white' 
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          <Settings className="w-5 h-5 mr-2" />
          Detection Settings
        </button>
      </div>

      {/* Show Settings View if selected */}
      {activeView === 'settings' ? (
        <AIFraudSettings />
      ) : (
        <>
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Alerts</p>
              <p className="text-2xl font-bold">{stats.total || 0}</p>
            </div>
            <Shield className="w-8 h-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-600 text-sm">Critical</p>
              <p className="text-2xl font-bold text-red-600">{stats.critical || 0}</p>
            </div>
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
        </div>

        <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-600 text-sm">High Risk</p>
              <p className="text-2xl font-bold text-orange-600">{stats.high || 0}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-orange-400" />
          </div>
        </div>

        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-600 text-sm">Medium Risk</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.medium || 0}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-yellow-400" />
          </div>
        </div>

        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 text-sm">Unreviewed</p>
              <p className="text-2xl font-bold text-blue-600">{stats.unreviewed || 0}</p>
            </div>
            <Eye className="w-8 h-8 text-blue-400" />
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg border border-gray-200 p-1 mb-6">
        <div className="flex space-x-1">
          {['pending_review', 'reviewed', 'all'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                filter === status 
                  ? 'bg-purple-600 text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {status.replace('_', ' ').charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-600 mb-4" />
            <p className="text-gray-600">Loading fraud alerts...</p>
          </div>
        ) : alerts.length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center border border-gray-200">
            <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Alerts</h3>
            <p className="text-gray-600">No suspicious activity detected</p>
          </div>
        ) : (
          alerts.map(alert => (
            <div 
              key={alert.id}
              className={`bg-white rounded-lg border-2 p-6 ${getRiskColor(alert.riskLevel)}`}
            >
              {/* Alert Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start">
                  <div className={`p-2 rounded-lg ${getRiskColor(alert.riskLevel)} mr-4`}>
                    {getRiskIcon(alert.riskLevel)}
                  </div>
                  <div>
                    <div className="flex items-center mb-1">
                      <h3 className="text-lg font-semibold text-gray-900 mr-3">
                        User: {alert.userId}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskColor(alert.riskLevel)}`}>
                        {alert.riskLevel} RISK - {alert.overallRiskScore}%
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Action: {alert.actionType} • {new Date(alert.createdAt?.seconds * 1000).toLocaleString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => toggleAlertExpansion(alert.id)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {expandedAlerts[alert.id] ? <ChevronUp /> : <ChevronDown />}
                </button>
              </div>

              {/* Alert Details */}
              {alert.alerts && alert.alerts.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Detected Issues:</h4>
                  <div className="space-y-2">
                    {alert.alerts.map((issue, index) => (
                      <div key={index} className="flex items-start">
                        <AlertTriangle className="w-4 h-4 text-orange-500 mr-2 mt-0.5" />
                        <div className="flex-1">
                          <span className="text-sm text-gray-700">{issue.message}</span>
                          <span className="ml-2 text-xs text-gray-500">({issue.score}% risk)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Expanded Risk Scores */}
              {expandedAlerts[alert.id] && alert.riskScores && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Risk Analysis Breakdown:</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(alert.riskScores).map(([key, value]) => (
                      <div key={key} className="bg-white p-3 rounded border border-gray-200">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-600">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                          <span className={`text-sm font-bold ${
                            value.score >= 80 ? 'text-red-600' : 
                            value.score >= 60 ? 'text-orange-600' : 
                            value.score >= 40 ? 'text-yellow-600' : 'text-green-600'
                          }`}>
                            {value.score}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              value.score >= 80 ? 'bg-red-600' : 
                              value.score >= 60 ? 'bg-orange-600' : 
                              value.score >= 40 ? 'bg-yellow-600' : 'bg-green-600'
                            }`}
                            style={{ width: `${value.score}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{value.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {!alert.reviewed && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    ⚠️ Warning Only - No automatic action taken
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => reviewAlert(alert.id, 'acknowledged')}
                      className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Acknowledge
                    </button>
                    <button
                      onClick={() => reviewAlert(alert.id, 'investigate')}
                      className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Investigate
                    </button>
                    <button
                      onClick={() => reviewAlert(alert.id, 'false_positive')}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      <UserCheck className="w-4 h-4 mr-1" />
                      False Positive
                    </button>
                  </div>
                </div>
              )}

              {/* Reviewed Status */}
              {alert.reviewed && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-600">
                      <Check className="w-4 h-4 mr-2 text-green-600" />
                      Reviewed: {alert.actionTaken}
                      {alert.reviewedAt && (
                        <span className="ml-2 text-gray-500">
                          • {new Date(alert.reviewedAt.seconds * 1000).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Information Panel */}
      <div className="mt-12 bg-blue-50 rounded-lg p-6 border border-blue-200">
        <div className="flex items-start">
          <AlertCircle className="w-6 h-6 text-blue-600 mr-3 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-blue-900 mb-2">About AI Fraud Detection</h3>
            <p className="text-blue-800 mb-3">
              This system monitors user behavior for suspicious patterns but <strong>does not automatically restrict users</strong>. 
              All alerts are for admin review only.
            </p>
            <div className="space-y-2 text-sm text-blue-700">
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span><strong>Account Velocity:</strong> Unusual spikes in activity or rapid actions</span>
              </div>
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span><strong>Referral Abuse:</strong> Self-referrals or circular referral patterns</span>
              </div>
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span><strong>Payment Anomaly:</strong> Failed payments, rapid attempts, or unusual amounts</span>
              </div>
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span><strong>Performance Recycling:</strong> Batch uploads or suspiciously fast deliveries</span>
              </div>
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span><strong>Rating Manipulation:</strong> Reciprocal ratings or suspicious perfect scores</span>
              </div>
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span><strong>Multiple Accounts:</strong> Same IP/device used for multiple accounts</span>
              </div>
            </div>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default AIFraudDashboard;